import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Book, Code, Shield, Key, FileText, Building2, Users, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Display URL = what user sees in browser (custom domain). Used for vendor-facing docs.
const DISPLAY_URL = typeof window !== 'undefined' ? window.location.origin : BACKEND_URL;

export default function APIDocsPage({ user, onLogout }) {
  const swaggerUrl = DISPLAY_URL.replace('/api', '') + '/docs';
  const redocUrl = DISPLAY_URL.replace('/api', '') + '/redoc';

  return (
    <Layout user={user} onLogout={onLogout} activePage="api-docs">
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">
            API Documentation
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Integrate My Shield compliance screening into your systems
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-6 border border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <Book className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Interactive API Docs</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Swagger UI - Test API endpoints directly in your browser
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => window.open(swaggerUrl, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Swagger UI
                </Button>
              </div>
            </div>
          </Card>

          <Card className="p-6 border border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">API Reference</h3>
                <p className="text-sm text-slate-600 mt-1">
                  ReDoc - Clean, readable API documentation
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => window.open(redocUrl, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open ReDoc
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Integration Guide */}
        <Card className="border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Code className="w-5 h-5" />
              Quick Start Guide
            </h2>
          </div>
          <div className="p-6 space-y-6">
            {/* Step 1 */}
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Key className="w-4 h-4 text-blue-600" />
                1. Get Your API Key
              </h3>
              <p className="text-sm text-slate-600 mt-2">
                Create an API key from the <a href="/integrations" className="text-blue-600 underline">API Keys</a> page. 
                Keep this key secure - it provides access to the screening API.
              </p>
            </div>

            {/* Step 2 */}
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-600" />
                2. Screen a Name
              </h3>
              <p className="text-sm text-slate-600 mt-2 mb-3">
                Use the external scan endpoint to check names against OFAC and PEP lists:
              </p>
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-slate-100">
{`curl -X POST "${DISPLAY_URL}/external/scan" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John Smith",
    "date_of_birth": "1980-01-15",
    "nationality": "US"
  }'`}
                </pre>
              </div>
            </div>

            {/* Step 3 */}
            <div>
              <h3 className="font-semibold text-slate-900">3. Handle the Response</h3>
              <p className="text-sm text-slate-600 mt-2 mb-3">
                The API returns detailed match information:
              </p>
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-slate-100">
{`{
  "scan_id": "uuid",
  "query_name": "John Smith",
  "status": "completed",
  "risk_level": "high",  // critical, high, medium, low, clear
  "matches": [
    {
      "matched_name": "SMITH, John",
      "source": "OFAC SDN",
      "score": 95.5,
      "match_type": "exact",
      "details": {
        "birth_date": "1980-01-15",
        "nationality": ["US"],
        "programs": ["SDGT"],
        ...
      }
    }
  ]
}`}
                </pre>
              </div>
            </div>

            {/* Risk Levels */}
            <div>
              <h3 className="font-semibold text-slate-900">Risk Level Definitions</h3>
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800">CRITICAL</span>
                  <span className="text-sm text-slate-600">Confirmed match - Name + DOB + Nationality all match</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 text-xs font-semibold rounded bg-rose-100 text-rose-800">HIGH</span>
                  <span className="text-sm text-slate-600">Strong match - Name ≥85% with partial identity match</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 text-xs font-semibold rounded bg-amber-100 text-amber-800">MEDIUM</span>
                  <span className="text-sm text-slate-600">Possible match - Name ≥75% match score</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">LOW</span>
                  <span className="text-sm text-slate-600">Weak match - Name ≥60% match score</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 text-xs font-semibold rounded bg-emerald-100 text-emerald-800">CLEAR</span>
                  <span className="text-sm text-slate-600">No significant matches found</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* === FOREX LINK INTEGRATION GUIDE === */}
        <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white">
          <div className="p-6 border-b border-indigo-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <ArrowRight className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Forex Link / Core System Integration</h2>
                <p className="text-xs text-slate-500 mt-0.5">Full KYC screening with identity verification — recommended for production</p>
              </div>
            </div>
            <span className="px-3 py-1 text-[10px] font-semibold tracking-wide bg-indigo-600 text-white rounded-full">RECOMMENDED</span>
          </div>

          <div className="p-6 space-y-8">
            {/* Endpoint */}
            <div>
              <p className="text-xs uppercase font-semibold tracking-wide text-slate-500 mb-2">Endpoint</p>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs text-emerald-300">
                POST {DISPLAY_URL}/api/v1/screen/customer
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="bg-white border border-slate-200 rounded p-3">
                  <p className="font-semibold text-slate-700 mb-1">Headers</p>
                  <code className="text-slate-600">Content-Type: application/json</code><br/>
                  <code className="text-slate-600">X-API-Key: YOUR_API_KEY</code>
                </div>
                <div className="bg-white border border-slate-200 rounded p-3">
                  <p className="font-semibold text-slate-700 mb-1">Authentication</p>
                  <p className="text-slate-600">Generate keys on the <a href="/integrations" className="text-indigo-600 underline">API Keys</a> page. Each key is scoped to your organization.</p>
                </div>
              </div>
            </div>

            {/* Individual */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-indigo-600" />
                <h3 className="font-semibold text-slate-900">1. Screen an Individual Customer</h3>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Send the customer&apos;s KYC data when opening accounts or processing transactions in Forex Link. Only <code className="bg-slate-100 px-1 rounded text-xs">full_name</code> is required; the more fields you supply, the higher the match accuracy.
              </p>
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-slate-100">
{`curl -X POST "${DISPLAY_URL}/api/v1/screen/customer" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "customer_id": "CUST-12345",
    "full_name": "Ahmad Rashid bin Abdullah",
    "date_of_birth": "1985-03-15",
    "nationality": "MY",
    "gender": "male",
    "id_type": "national_id",
    "id_number": "850315-08-1234"
  }'`}
                </pre>
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs border border-slate-200 rounded">
                  <thead className="bg-slate-50">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-semibold text-slate-700">Field</th>
                      <th className="px-3 py-2 font-semibold text-slate-700">Required</th>
                      <th className="px-3 py-2 font-semibold text-slate-700">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr><td className="px-3 py-2 font-mono">full_name</td><td className="px-3 py-2 text-rose-600 font-semibold">Yes</td><td className="px-3 py-2 text-slate-600">Customer&apos;s full legal name</td></tr>
                    <tr><td className="px-3 py-2 font-mono">date_of_birth</td><td className="px-3 py-2 text-amber-600 font-semibold">Recommended</td><td className="px-3 py-2 text-slate-600">Format <code>YYYY-MM-DD</code>. Enables identity verification</td></tr>
                    <tr><td className="px-3 py-2 font-mono">nationality</td><td className="px-3 py-2 text-amber-600 font-semibold">Recommended</td><td className="px-3 py-2 text-slate-600">ISO-2 code (<code>MY</code>, <code>US</code>, <code>SG</code>) — boosts accuracy</td></tr>
                    <tr><td className="px-3 py-2 font-mono">gender</td><td className="px-3 py-2 text-slate-500">Optional</td><td className="px-3 py-2 text-slate-600"><code>male</code> / <code>female</code></td></tr>
                    <tr><td className="px-3 py-2 font-mono">id_number</td><td className="px-3 py-2 text-slate-500">Optional</td><td className="px-3 py-2 text-slate-600">Stored for audit trail</td></tr>
                    <tr><td className="px-3 py-2 font-mono">customer_id</td><td className="px-3 py-2 text-slate-500">Optional</td><td className="px-3 py-2 text-slate-600">Your internal ID for cross-referencing</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Company */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-indigo-600" />
                <h3 className="font-semibold text-slate-900">2. Screen a Company</h3>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Same endpoint. Use the company&apos;s legal name and registration number. Skip <code className="bg-slate-100 px-1 rounded text-xs">date_of_birth</code>, <code className="bg-slate-100 px-1 rounded text-xs">nationality</code>, and <code className="bg-slate-100 px-1 rounded text-xs">gender</code> — they don&apos;t apply to entities.
              </p>
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-slate-100">
{`curl -X POST "${DISPLAY_URL}/api/v1/screen/customer" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "customer_id": "CORP-789",
    "full_name": "ABC Trading Sdn Bhd",
    "id_type": "company_registration",
    "id_number": "202301012345",
    "country": "MY"
  }'`}
                </pre>
              </div>
              <p className="text-xs text-slate-500 mt-2 italic">
                Names containing company suffixes (Sdn Bhd, Bhd, Pte Ltd, Ltd, Inc, Corp, etc.) are automatically matched against organization watchlists (OFAC sanctioned entities, UN-listed companies).
              </p>
            </div>

            {/* Beneficial Owners */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-indigo-600" />
                <h3 className="font-semibold text-slate-900">3. Screen Beneficial Owners</h3>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                AML best practice: screen each beneficial owner (≥25% ownership) as a separate individual scan, in addition to the company itself. Tag each BO with the parent company&apos;s ID for traceability.
              </p>
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-slate-100">
{`# Step 1: Scan the company
POST /api/v1/screen/customer
{
  "customer_id": "CORP-789",
  "full_name": "ABC Trading Sdn Bhd",
  "id_number": "202301012345"
}

# Step 2: Scan each beneficial owner (loop)
POST /api/v1/screen/customer
{
  "customer_id": "BO-of-CORP-789-1",
  "full_name": "Tan Wei Ming",
  "date_of_birth": "1980-05-12",
  "nationality": "MY",
  "id_number": "800512-10-5678"
}

POST /api/v1/screen/customer
{
  "customer_id": "BO-of-CORP-789-2",
  "full_name": "Lim Hui Fen",
  "date_of_birth": "1975-11-30",
  "nationality": "SG",
  "id_number": "S7511301Z"
}

# Step 3: Overall risk = HIGHEST risk level across company + all BOs`}
                </pre>
              </div>
            </div>

            {/* Response */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">4. Response Format</h3>
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-slate-100">
{`{
  "screening_id": "uuid",
  "transaction_id": null,
  "customer_id": "CUST-12345",
  "screened_name": "Ahmad Rashid bin Abdullah",
  "result": "REVIEW",
  "risk_score": 87,
  "risk_level": "high",
  "recommendation": "Manual review required",
  "matches": [
    {
      "matched_name": "RASHID, Ahmad",
      "source": "OFAC SDN",
      "match_score": 92.5,
      "match_type": "fuzzy",
      "programs": ["SDGT"],
      "countries": ["MY"],
      "additional_info": {
        "identity_verification": {
          "verdict": "PROBABLE_MATCH",
          "match_factors": {
            "dob": { "result": "match" },
            "nationality": { "result": "match" }
          }
        }
      }
    }
  ],
  "screened_at": "2026-02-15T10:30:00Z"
}`}
                </pre>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                <strong>result</strong>: PASS / REVIEW / FAIL &nbsp;|&nbsp; <strong>risk_score</strong>: 0-100 &nbsp;|&nbsp; <strong>risk_level</strong>: clear / low / medium / high / critical
              </p>
            </div>

            {/* Decision Matrix */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">5. Decision Logic for Forex Link</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-slate-200 rounded">
                  <thead className="bg-slate-50">
                    <tr className="text-left">
                      <th className="px-4 py-2.5 font-semibold text-slate-700">risk_level</th>
                      <th className="px-4 py-2.5 font-semibold text-slate-700">Recommended Action in Forex Link</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr>
                      <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-xs font-semibold rounded bg-emerald-100 text-emerald-800">clear</span></td>
                      <td className="px-4 py-2.5 text-slate-600">Auto-approve transaction</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800">low</span></td>
                      <td className="px-4 py-2.5 text-slate-600">Auto-approve, log for periodic review</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-100 text-amber-800">medium</span></td>
                      <td className="px-4 py-2.5 text-slate-600">Soft-flag, compliance team reviews within 24h</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-xs font-semibold rounded bg-rose-100 text-rose-800">high</span></td>
                      <td className="px-4 py-2.5 text-slate-600"><strong>Block</strong> + immediate compliance review</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-800">critical</span></td>
                      <td className="px-4 py-2.5 text-slate-600"><strong>Block</strong> + escalate + consider filing SAR (Suspicious Activity Report)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Useful Extras */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="font-semibold text-indigo-900 text-sm mb-2">Useful Extras</h3>
              <ul className="text-xs text-indigo-800 space-y-1.5 list-disc list-inside">
                <li><strong>Bulk screening:</strong> <code className="bg-white px-1 rounded">POST /api/v1/screen/batch</code> — screen up to 100 customers per call (e.g., overnight re-screening)</li>
                <li><strong>Scan history:</strong> <code className="bg-white px-1 rounded">GET /api/v1/screenings?customer_id=CUST-12345</code> — retrieve past scans for audit</li>
                <li><strong>Rate limits:</strong> Response includes <code className="bg-white px-1 rounded">X-RateLimit-Remaining</code> headers — Forex Link should honor them</li>
                <li><strong>Webhooks:</strong> Pass <code className="bg-white px-1 rounded">callback_url</code> in the request to receive results asynchronously</li>
              </ul>
            </div>
          </div>
        </Card>


        {/* Available Endpoints */}
        <Card className="border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900">Available Endpoints</h2>
          </div>
          <div className="divide-y divide-slate-200">
            <div className="p-4 flex items-center gap-4">
              <span className="px-2 py-1 text-xs font-mono font-semibold rounded bg-indigo-100 text-indigo-800">POST</span>
              <code className="text-sm font-mono text-slate-700">/api/v1/screen/customer</code>
              <span className="text-sm text-slate-500">Full KYC screening (recommended for Forex Link)</span>
            </div>
            <div className="p-4 flex items-center gap-4">
              <span className="px-2 py-1 text-xs font-mono font-semibold rounded bg-indigo-100 text-indigo-800">POST</span>
              <code className="text-sm font-mono text-slate-700">/api/v1/screen/batch</code>
              <span className="text-sm text-slate-500">Bulk KYC screening (up to 100 per call)</span>
            </div>
            <div className="p-4 flex items-center gap-4">
              <span className="px-2 py-1 text-xs font-mono font-semibold rounded bg-blue-100 text-blue-800">GET</span>
              <code className="text-sm font-mono text-slate-700">/api/v1/screenings</code>
              <span className="text-sm text-slate-500">List screening history with filters</span>
            </div>
            <div className="p-4 flex items-center gap-4">
              <span className="px-2 py-1 text-xs font-mono font-semibold rounded bg-emerald-100 text-emerald-800">POST</span>
              <code className="text-sm font-mono text-slate-700">/api/external/scan</code>
              <span className="text-sm text-slate-500">Simple name-only scan (legacy)</span>
            </div>
            <div className="p-4 flex items-center gap-4">
              <span className="px-2 py-1 text-xs font-mono font-semibold rounded bg-emerald-100 text-emerald-800">POST</span>
              <code className="text-sm font-mono text-slate-700">/api/scan/bulk</code>
              <span className="text-sm text-slate-500">Screen multiple names (batch)</span>
            </div>
            <div className="p-4 flex items-center gap-4">
              <span className="px-2 py-1 text-xs font-mono font-semibold rounded bg-blue-100 text-blue-800">GET</span>
              <code className="text-sm font-mono text-slate-700">/api/scans</code>
              <span className="text-sm text-slate-500">Get scan history</span>
            </div>
            <div className="p-4 flex items-center gap-4">
              <span className="px-2 py-1 text-xs font-mono font-semibold rounded bg-blue-100 text-blue-800">GET</span>
              <code className="text-sm font-mono text-slate-700">/api/watchlist/stats</code>
              <span className="text-sm text-slate-500">Get watchlist statistics</span>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
